import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter, faChevronDown, faChevronRight, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import Drawer from "components/drawerSlide/drawer";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";

import {
  IDepartmentNode,
  ProductFilterValues,
  countProductFilters,
  emptyProductFilters,
  nodeKey,
  selectedDepartmentKeys,
  toggleDepartmentNode,
} from "./productFilterUtils";

import "./productFiltersDrawer.scss";

const INITIAL_VISIBLE_OPTIONS = 5;

interface IOption {
  id: number;
  title: string;
}

interface IProps {
  open: boolean;
  appliedFilters: ProductFilterValues;
  productTypes: IOption[];
  vendors: IOption[];
  departmentTree: IDepartmentNode[];
  canUseAllFacilitiesFilter: boolean;
  onClose: () => void;
  onApply: (filters: ProductFilterValues) => void;
}

function matchesSearch(title: string, search: string) {
  return title.toLowerCase().includes(search);
}

function filterTree(nodes: IDepartmentNode[], search: string): IDepartmentNode[] {
  if (!search) {
    return nodes;
  }
  return nodes
    .map(node => ({
      ...node,
      children: matchesSearch(node.title, search) ? node.children : filterTree(node.children, search),
    }))
    .filter(node => matchesSearch(node.title, search) || node.children.length > 0);
}

export default function ProductFiltersDrawer(props: IProps) {
  const { open, appliedFilters, productTypes, vendors, departmentTree, canUseAllFacilitiesFilter, onClose, onApply } =
    props;
  const { t } = useTranslation();

  const [draft, setDraft] = useState<ProductFilterValues>(emptyProductFilters());
  const [search, setSearch] = useState("");
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [showAllVendors, setShowAllVendors] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setDraft(appliedFilters);
      setSearch("");
      setShowAllTypes(false);
      setShowAllVendors(false);
      setExpandedNodes([]);
    }
  }, [open, appliedFilters]);

  const normalizedSearch = search.trim().toLowerCase();
  const filterCount = countProductFilters(draft);
  const selectedKeys = selectedDepartmentKeys(draft);

  const filteredTypes = useMemo(
    () => (productTypes ?? []).filter(type => matchesSearch(type.title, normalizedSearch)),
    [productTypes, normalizedSearch],
  );
  const filteredVendors = useMemo(
    () => (vendors ?? []).filter(vendor => matchesSearch(vendor.title, normalizedSearch)),
    [vendors, normalizedSearch],
  );
  const filteredTree = useMemo(() => filterTree(departmentTree, normalizedSearch), [departmentTree, normalizedSearch]);

  function toggleValue<K extends "type" | "vendor_id">(key: K, value: ProductFilterValues[K][number]) {
    setDraft(prev => {
      const current = prev[key] as Array<typeof value>;
      return {
        ...prev,
        [key]: current.includes(value) ? current.filter(item => item !== value) : [...current, value],
      };
    });
  }

  function handleApply() {
    onApply(draft);
    onClose();
  }

  function renderOptionList<K extends "type" | "vendor_id">(
    key: K,
    label: string,
    options: IOption[],
    showAll: boolean,
    onShowAll: () => void,
    valueOf: (option: IOption) => ProductFilterValues[K][number],
  ) {
    if (options.length === 0) {
      return null;
    }
    const visibleOptions = showAll ? options : options.slice(0, INITIAL_VISIBLE_OPTIONS);
    const hiddenCount = options.length - visibleOptions.length;

    return (
      <Drawer.Section bordered>
        <p className="product-filters-drawer-label">{label}</p>
        <div className="product-filters-drawer-list">
          {visibleOptions.map(option => (
            <Checkbox
              key={option.id}
              id={`product-filter-${key}-${option.id}`}
              size="small"
              fullWidth
              label={option.title}
              checked={(draft[key] as Array<string | number>).includes(valueOf(option))}
              onChange={() => toggleValue(key, valueOf(option))}
            />
          ))}
          {hiddenCount > 0 ? (
            <button type="button" className="product-filters-drawer-show-more" onClick={onShowAll}>
              {t("Show {{count}} more", { count: hiddenCount })}
            </button>
          ) : null}
        </div>
      </Drawer.Section>
    );
  }

  function renderDepartmentNode(node: IDepartmentNode, ancestorSelected: boolean, depth = 0): JSX.Element {
    const key = nodeKey(node);
    const checked = ancestorSelected || selectedKeys.has(key);
    const indeterminate = !checked && node.children.length > 0 && hasSelectedDescendant(node.children, selectedKeys);
    const expanded = Boolean(normalizedSearch) || expandedNodes.includes(key);

    return (
      <li key={key}>
        <div
          className={classNames("product-filters-drawer-tree-row", {
            "is-active": depth === 0 && (checked || indeterminate),
          })}
        >
          {node.children.length > 0 ? (
            <button
              type="button"
              className="product-filters-drawer-tree-toggle"
              aria-expanded={expanded}
              aria-label={
                expanded ? t("Collapse {{title}}", { title: node.title }) : t("Expand {{title}}", { title: node.title })
              }
              onClick={() =>
                setExpandedNodes(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]))
              }
            >
              <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
            </button>
          ) : (
            <span className="product-filters-drawer-tree-toggle" aria-hidden="true" />
          )}
          <Checkbox
            id={`product-filter-${key}`}
            size="small"
            fullWidth
            label={node.title}
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => setDraft(prev => toggleDepartmentNode(departmentTree, prev, node))}
          />
        </div>
        {expanded && node.children.length > 0 ? (
          <ul className="product-filters-drawer-tree">
            {node.children.map(child => renderDepartmentNode(child, checked, depth + 1))}
          </ul>
        ) : null}
      </li>
    );
  }

  return (
    <Drawer
      className="product-filters-drawer"
      open={open}
      title={t("Filter products by")}
      subtitle={t("{{count}} filters selected", { count: filterCount })}
      headerLeading={
        <span className="product-filters-drawer-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBarsFilter} />
        </span>
      }
      onClose={onClose}
      onOk={handleApply}
      okText={filterCount > 0 ? t("Apply {{count}} filters", { count: filterCount }) : t("Apply filters")}
      showCancel
      cancelText={t("Cancel")}
      cancelButtonType="secondary"
      secondaryActions={[{ label: t("Clear all"), action: () => setDraft(emptyProductFilters()), buttonType: "text" }]}
      backDropClose
    >
      <Drawer.Section>
        <Input
          id="product-filter-search"
          labelHidden
          icon={faMagnifyingGlass}
          placeholder={t("Search by type, vendor, department...")}
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </Drawer.Section>

      {renderOptionList(
        "type",
        t("Product types"),
        filteredTypes,
        showAllTypes,
        () => setShowAllTypes(true),
        option => option.title,
      )}

      {!normalizedSearch ? (
        <Drawer.Section bordered>
          <p className="product-filters-drawer-label">{t("Availability channel")}</p>
          <div className="product-filters-drawer-list">
            <Checkbox
              id="product-filter-channel-all"
              size="small"
              fullWidth
              label={t("All products")}
              checked={draft.sales_channel_type === null}
              onChange={() => setDraft(prev => ({ ...prev, sales_channel_type: null }))}
            />
            <Checkbox
              id="product-filter-channel-online"
              size="small"
              fullWidth
              label={t("Online store")}
              checked={draft.sales_channel_type === "online_store"}
              onChange={() =>
                setDraft(prev => ({
                  ...prev,
                  sales_channel_type: prev.sales_channel_type === "online_store" ? null : "online_store",
                }))
              }
            />
            {canUseAllFacilitiesFilter ? (
              <Checkbox
                id="product-filter-all-facilities"
                size="small"
                fullWidth
                label={t("Include products from other facilities")}
                checked={draft.all_facilities}
                onChange={() => setDraft(prev => ({ ...prev, all_facilities: !prev.all_facilities }))}
              />
            ) : null}
          </div>
        </Drawer.Section>
      ) : null}

      {renderOptionList(
        "vendor_id",
        t("Vendors"),
        filteredVendors,
        showAllVendors,
        () => setShowAllVendors(true),
        option => option.id,
      )}

      {filteredTree.length > 0 ? (
        <Drawer.Section>
          <p className="product-filters-drawer-label">{t("Departments")}</p>
          <ul className="product-filters-drawer-tree">{filteredTree.map(node => renderDepartmentNode(node, false))}</ul>
        </Drawer.Section>
      ) : null}

      {normalizedSearch && filteredTypes.length === 0 && filteredVendors.length === 0 && filteredTree.length === 0 ? (
        <Drawer.Section>
          <p className="product-filters-drawer-empty">{t("No filters found")}</p>
        </Drawer.Section>
      ) : null}
    </Drawer>
  );
}

function hasSelectedDescendant(nodes: IDepartmentNode[], selectedKeys: Set<string>): boolean {
  return nodes.some(node => selectedKeys.has(nodeKey(node)) || hasSelectedDescendant(node.children, selectedKeys));
}
